import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalanceWalletOutlined as ResteIcon,
  CalendarMonthOutlined as PlanIcon,
  Description as OffresIcon,
  EventAvailableOutlined as SoonIcon,
  Handyman as ServicesIcon,
  HistoryOutlined as HistoryIcon,
  HourglassEmptyOutlined as AvanceIcon,
  PaymentsOutlined as PayIcon,
  PeopleOutline as ClientsIcon,
  WarningAmberOutlined as AlertIcon,
} from '@mui/icons-material';
import { GlassSurface } from '../../../components/GlassDashboard';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useComptoirDashboard } from '../../../hooks/queries/useComptoir';
import { ComptoirDashboardService } from '../../../types/comptoir';
import { COLOR, PAIEMENT_TONE, Panel, StateChip, dh, fDate, fmtPhone } from '../ui';

const fDT = (d: string) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/** A clickable row of a dashboard panel. */
const Row: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <ButtonBase
    onClick={onClick} disabled={!onClick}
    sx={{
      width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1,
      borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 },
      '&:hover': { bgcolor: onClick ? alpha(COLOR, 0.04) : undefined },
    }}
  >
    {children}
  </ButtonBase>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{text}</Typography>
);

const Ref: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{children}</Box>
);

/** Comptoir landing page: offres, services, money still owed and what needs attention (after the prototype). */
const ComptoirDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { canAccessModule } = usePermissions();
  const { data: d, isLoading, error } = useComptoirDashboard();
  const k = d?.kpis;
  const openSrv = (s: { id: number }) => navigate(`/comptoir/services/${s.id}`);
  const openClient = d?.acces.clients ? (id: number) => navigate(`/comptoir/clients/${id}`) : undefined;

  const srvLine = (s: ComptoirDashboardService, sub: React.ReactNode, right?: React.ReactNode) => (
    <Row key={`${s.id}-${s.alerte || ''}`} onClick={() => openSrv(s)}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap><Ref>{s.reference}</Ref> · {s.titre}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">{sub}</Typography>
      </Box>
      {right}
    </Row>
  );

  const kpis = [
    d?.offres_mois && {
      title: 'Offres ce mois', value: String(d.offres_mois.count), color: '#607D8B', icon: <OffresIcon />,
      sub: d.offres_mois.suppressions ? `${d.offres_mois.suppressions} suppression(s) à valider` : 'offres enregistrées ce mois',
      to: '/comptoir/offres',
    },
    k && {
      title: 'Avance attendue', value: String(k.avance_attendue), color: '#ED6C02', icon: <AvanceIcon />,
      sub: `${dh(k.avance_attendue_montant)} de travaux en attente`, to: '/comptoir/services?paiement=attente',
    },
    k && {
      title: 'Services & projets en cours', value: String(k.ouverts), color: COLOR, icon: <ServicesIcon />,
      sub: `${k.nouveaux_semaine} nouveau(x) cette semaine${k.en_retard ? ` · ${k.en_retard} en retard` : ''}`,
      to: '/comptoir/services?etat=ouverts',
    },
    k && {
      title: 'À planifier en production', value: String(k.a_planifier), color: '#0288D1', icon: <PlanIcon />,
      sub: 'pas encore dans Gestion Planning', to: canAccessModule('Production') ? '/production/planning' : undefined,
    },
    k && {
      title: 'Encaissé ce mois', value: dh(k.encaisse_mois), color: '#2E7D32', icon: <PayIcon />,
      sub: `${k.paiements_mois} paiement(s)`, to: '/comptoir/finance',
    },
    k && {
      title: 'Montants restants', value: dh(k.reste), color: '#D32F2F', icon: <ResteIcon />,
      sub: `${k.reste_services} service(s) non soldé(s)`, to: '/comptoir/finance',
    },
  ].filter(Boolean) as { title: string; value: string; sub: string; color: string; icon: React.ReactNode; to?: string }[];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Tableau de bord</Typography>
        <Typography variant="body2" color="text.secondary">
          Vue commerciale et financière du Comptoir : offres, services et projets, paiements et montants restants.
        </Typography>
      </Box>

      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{extractErrorMessage(error)}</Alert>}

      {/* ---------- KPIs ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {(isLoading ? Array.from({ length: 6 }) : kpis).map((c: any, i) => (
          <Grid item xs={12} sm={6} md={4} xl={2} key={c?.title || i}>
            <ButtonBase onClick={c?.to ? () => navigate(c.to) : undefined} disabled={!c?.to}
              sx={{ width: '100%', height: '100%', borderRadius: 2, textAlign: 'left', display: 'block' }}>
              <GlassSurface accentColor={c?.color || COLOR} sx={{ p: 2, height: '100%' }}>
                {c ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary">{c.title}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{c.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
                    </Box>
                    <Box sx={{ color: c.color, '& svg': { fontSize: 30 } }}>{c.icon}</Box>
                  </Stack>
                ) : <><Skeleton width="60%" /><Skeleton width={90} height={36} /><Skeleton width="80%" /></>}
              </GlassSurface>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>

      {d && !d.acces.services && (
        <Alert severity="info">Le détail des services, paiements et soldes demande l'accès « Services & Projets ».</Alert>
      )}

      {d?.acces.services && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={6}>
            <Panel title="À surveiller" icon={<AlertIcon />} count={d.alertes?.length || 0}>
              {d.alertes?.length ? d.alertes.slice(0, 10).map((s) => srvLine(s,
                <><Box component="span" sx={{ color: s.niveau === 'error' ? 'error.main' : 'warning.main', fontWeight: 600 }}>{s.alerte}</Box> · {s.client_nom}</>,
              )) : <Empty text="Aucun retard, avance manquante, matière en attente ni solde après livraison." />}
            </Panel>
            <Panel title="À planifier en production" icon={<PlanIcon />} count={d.kpis?.a_planifier || 0}>
              {d.a_planifier?.length ? d.a_planifier.map((s) => srvLine(s,
                `${s.client_nom || '—'} · deadline ${fDate(s.deadline)}`,
                <StateChip label={s.paiement_label} t={PAIEMENT_TONE[s.paiement]} small />,
              )) : <Empty text="Tous les services en cours sont dans Gestion Planning." />}
            </Panel>
            <Panel title="Livraisons promises (7 jours)" icon={<SoonIcon />} count={d.livraisons_semaine?.length || 0}>
              {d.livraisons_semaine?.length ? d.livraisons_semaine.map((s) => srvLine(s,
                `${fDate(s.deadline)} · ${s.client_nom || '—'}`,
                <Chip label={s.etat_label} size="small" variant="outlined" />,
              )) : <Empty text="Aucune livraison promise dans les 7 jours." />}
            </Panel>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Panel title="Derniers paiements" icon={<PayIcon />}>
              {d.derniers_paiements?.length ? d.derniers_paiements.map((p) => (
                <Row key={p.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap><Ref>{p.reference}</Ref> · {p.client_nom || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {fDate(p.date)} · {p.mode}{p.parts ? ` · ${p.parts}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main', whiteSpace: 'nowrap' }}>{dh(p.montant)}</Typography>
                </Row>
              )) : <Empty text="Aucun paiement enregistré." />}
            </Panel>
            <Panel title="Soldes clients" icon={<ClientsIcon />} count={d.soldes_clients?.length || 0}>
              {d.soldes_clients?.length ? d.soldes_clients.map((c) => (
                <Row key={c.client_id} onClick={openClient ? () => openClient(c.client_id) : undefined}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{c.client_nom || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.services} service(s){c.telephone ? ` · ${fmtPhone(c.telephone)}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'error.main', whiteSpace: 'nowrap' }}>{dh(c.reste)}</Typography>
                </Row>
              )) : <Empty text="Tous les clients sont à jour." />}
            </Panel>
            <Panel title="Dernières opérations" icon={<HistoryIcon />}>
              {d.dernieres_operations?.length ? d.dernieres_operations.map((o, i) => (
                <Row key={i} onClick={o.service_id ? () => openSrv({ id: o.service_id as number }) : undefined}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{o.reference && <><Ref>{o.reference}</Ref> · </>}{o.description}</Typography>
                    <Typography variant="caption" color="text.secondary">{fDT(o.date)} · {o.user_name || '—'}</Typography>
                  </Box>
                </Row>
              )) : <Empty text="Rien n'a encore été enregistré." />}
            </Panel>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ComptoirDashboard;
